import { useCallback, useMemo, useState } from "react"

import { useGetSuppliers } from "@/hooks/useGetSuppliers"
import type { AutocompleteOption } from "@/types/form-fields"
import useDebounce from "@/utils/useDebounce"

interface UseSupplierSearchReturn {
  supplierOptions: AutocompleteOption<string>[]
  isFetching: boolean
  onSearch: (term: string) => void
}

export const useSupplierSearch = (): UseSupplierSearchReturn => {
  const [searchTerm, setSearchTerm] = useState("")
  const { suppliers, isFetching: isSupplierListFetching } = useGetSuppliers({
    query: searchTerm,
    companyIds: [],
    getAllSuppliers: true,
  })

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const filteredSuppliers = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return suppliers
    }

    return suppliers.filter((supplier) => supplier.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
  }, [debouncedSearchTerm, suppliers])

  const supplierOptions: AutocompleteOption<string>[] = useMemo(
    () =>
      filteredSuppliers.map((supplier) => ({
        id: supplier.companyId,
        label: `${supplier.name}`,
      })),
    [filteredSuppliers]
  )

  const onSearch = useCallback((term: string) => {
    setSearchTerm(term)
  }, [])

  return {
    supplierOptions,
    isFetching: isSupplierListFetching,
    onSearch,
  }
}
